import { ComponentType, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, computed, inject, viewChild, ViewContainerRef } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { AbstractFilterMenuComponent } from '@connect-services/client/components/abstract-filter-menu/abstract-filter-menu.component';
import { FilterResolverService } from '@connect-services/client/services/filter-resolver.service';
import { MapFilterPermissionsService } from '@connect-services/client/services/map-filter-permissions.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-filter-menu',
    templateUrl: './filter-menu.component.html',
})
export class FilterMenuComponent {
    protected readonly activeFilterCount = computed(() =>
        this.filterResolverService.currentService()?.filterElementsCount(),
    );
    protected readonly buttonLabel = computed(() => {
        return this.activeFilterCount()
            ? this.translocoService.translate('activeFilter', { COUNT: this.activeFilterCount() })
            : this.translocoService.translate('addFilter');
    });
    protected readonly buttonTrigger = viewChild.required<ButtonComponent>('menuTrigger');
    protected readonly ButtonType = ButtonType;
    protected readonly filterResolverService = inject(FilterResolverService);
    protected readonly mapFilterPermissionsService = inject(MapFilterPermissionsService);

    private overlay = inject(Overlay);
    private overlayRef: OverlayRef | null = null;
    private positionStrategy!: FlexibleConnectedPositionStrategy;
    private readonly translocoService = inject(TranslocoService);
    private readonly viewContainerRef = inject(ViewContainerRef);

    public toggleMenu(): void {
        if (!this.mapFilterPermissionsService.canRead()) {
            return;
        }

        if (this.overlayRef) {
            this.closeOverlay();

            return;
        }

        const filterType = this.filterResolverService.currentFilterType();
        const component: ComponentType<AbstractFilterMenuComponent> | undefined =
            this.filterResolverService.currentComponent();

        if (!filterType || !component) {
            return;
        }

        if (!this.positionStrategy) {
            this.positionStrategy = this.overlay
                .position()
                .flexibleConnectedTo(this.buttonTrigger().nativeElement)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                ])
                .withFlexibleDimensions(false)
                .withPush(false);
        } else {
            this.positionStrategy.setOrigin(this.buttonTrigger().nativeElement);
        }

        this.overlayRef = this.overlay.create({
            backdropClass: 'map-backdrop',
            hasBackdrop: true,
            panelClass: 'filter-panel',
            positionStrategy: this.positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());
        this.overlayRef.keydownEvents().subscribe((event) => event.key === 'Escape' && this.closeOverlay());
        this.overlayRef.detachments().subscribe(() => (this.overlayRef = null));

        const portal = new ComponentPortal(component, this.viewContainerRef);
        const componentRef = this.overlayRef.attach(portal);

        componentRef.instance.closeFilter.subscribe(() => this.closeOverlay());
    }

    private closeOverlay(): void {
        if (!this.overlayRef) {
            return;
        }

        this.overlayRef?.dispose();
        this.overlayRef = null;
    }
}
